/**
 * SafeResponse extends the native Response class to safely handle multiple
 * calls to body-consuming methods (json, text, arrayBuffer, blob, bytes,
 * formData) — including concurrent calls before the first one resolves,
 * and mixed calls across different methods on the same response.
 *
 * Strategy
 * ────────
 * The native Response body is a one-shot ReadableStream; once consumed it
 * cannot be re-read.  SafeResponse works around this by:
 *
 *  1. Keeping a dedicated Response *clone* per body-method so that each
 *     method type always has a fresh, unconsumed stream to read from.
 *
 *  2. Caching the settled promise from the very first call to each method.
 *     Every subsequent call — sequential or concurrent, awaited or not —
 *     receives that same promise, so the underlying stream is only ever
 *     consumed once per method.
 */

type BodyMethod = 'json' | 'text' | 'arrayBuffer' | 'blob' | 'bytes' | 'formData'

type BodyMethodReturnType<M extends BodyMethod> = M extends 'json'
  ? unknown
  : M extends 'text'
    ? string
    : M extends 'arrayBuffer'
      ? ArrayBuffer
      : M extends 'blob'
        ? Blob
        : M extends 'bytes'
          ? Uint8Array<ArrayBuffer>
          : M extends 'formData'
            ? FormData
            : never

interface CacheEntry<T> {
  clone: Response
  promise: Promise<T>
}

class SafeResponse extends Response {
  readonly #cache = new Map<BodyMethod, CacheEntry<unknown>>()

  /**
   * Returns a deduped, cached promise for the given body-reading method.
   * All calls — sequential or concurrent — resolve to the same value.
   */
  #read<M extends BodyMethod>(method: M): Promise<BodyMethodReturnType<M>> {
    if (!this.#cache.has(method)) {
      // Create a fresh clone exclusively for this method so it gets its own
      // ReadableStream, completely isolated from clones for other methods.
      const clone = this.clone()
      const promise = (clone[method] as () => Promise<BodyMethodReturnType<M>>)()
      this.#cache.set(method, { clone, promise })
    }

    return this.#cache.get(method)!.promise as Promise<BodyMethodReturnType<M>>
  }

  override json<T = unknown>(): Promise<T> {
    return this.#read('json') as Promise<T>
  }

  override text(): Promise<string> {
    return this.#read('text')
  }

  override arrayBuffer(): Promise<ArrayBuffer> {
    return this.#read('arrayBuffer')
  }

  override blob(): Promise<Blob> {
    return this.#read('blob')
  }

  override bytes(): Promise<Uint8Array<ArrayBuffer>> {
    return this.#read('bytes')
  }

  override formData(): Promise<FormData> {
    return this.#read('formData')
  }
}

export { SafeResponse }
